import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import './App.css';
import { navigationConfig, bottomNavigationConfig } from './config/navigation';
import PageWrapper from './components/PageWrapper/PageWrapper';
import Login from './pages/Login/Login';
import Promotions from './pages/Promotions/Promotions';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import SessionExpiredDialog from './components/Dialogs/SessionExpiredDialog';

// Import our color palette
import colors from './themes/colors';

// Create a theme
const theme = createTheme({
  typography: {
    fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
  },
  palette: {
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
      contrastText: colors.primary.contrast
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
      contrastText: colors.secondary.contrast
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
      tabs: colors.background.tabs,
      sidebar: colors.background.sidebar
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled
    },
    error: {
      main: colors.status.error
    },
    warning: {
      main: colors.status.warning
    },
    info: {
      main: colors.status.info
    },
    success: {
      main: colors.status.success
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        body {
          height: 100vh;
          width: 100vw;
          overflow: hidden;
        }
      `,
    },
  },
});

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Main app layout with authentication
const AppLayout = () => {
  const [activePage, setActivePage] = useState('insights');
  const [pageTabs, setPageTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [rightSidebar, setRightSidebar] = useState(null);
  const [overlayMode, setOverlayMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // All nav items for lookup
  const allNavItems = React.useMemo(() => [...navigationConfig, ...bottomNavigationConfig], []);

  // Single source of truth: derive page + tab from URL
  React.useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const pagePath = pathParts[0] || 'insights';
    const tabSlug = pathParts[1] || null;

    const pageConfig = allNavItems.find(item => item.id === pagePath);
    const tabs = pageConfig?.tabs || [];

    let tabIndex = 0;
    if (tabSlug) {
      const slugIndex = tabs.findIndex(tab => tab.slug === tabSlug);
      if (slugIndex >= 0) tabIndex = slugIndex;
    } else {
      // Fallback: check location.state?.defaultTab for legacy navigation
      const requested = location.state?.defaultTab;
      if (requested) {
        const stateIndex = tabs.findIndex(tab => {
          const key = (tab.labelKey || tab.label || '').toString().toLowerCase();
          return key === requested.toString().toLowerCase();
        });
        if (stateIndex >= 0) tabIndex = stateIndex;
      }
    }

    setActivePage(pagePath);
    setPageTabs(tabs);
    setActiveTab(tabIndex);
  }, [location.pathname, location.state, allNavItems]);

  // When header tab changes, update URL with slug
  const handleHeaderTabChange = React.useCallback((tabIndex) => {
    setActiveTab(tabIndex);
    const pageConfig = allNavItems.find(item => item.id === activePage);
    const tab = pageConfig?.tabs?.[tabIndex];
    if (tab?.slug) {
      navigate(`/${activePage}/${tab.slug}`, { replace: true });
    }
  }, [activePage, allNavItems, navigate]);

  // Legacy handler (kept for compatibility)
  const handlePageChange = (page) => {
    setActivePage(page);
    const pageConfig = allNavItems.find(item => item.id === page);
    const tabs = pageConfig?.tabs || [];
    setPageTabs(tabs);
    setActiveTab(0);
  };

  // Listen for sidebar updates from child components
  React.useEffect(() => {
    const handleSidebarUpdate = () => {
      const sidebarData = window.currentPageSidebar;
      
      if (sidebarData && typeof sidebarData === 'object' && sidebarData.content) {
        // Handle object with content and overlay flag
        setRightSidebar(sidebarData.content);
        setOverlayMode(sidebarData.overlayMode || false);
      } else {
        // Handle direct component (legacy behavior)
        setRightSidebar(sidebarData || null);
        setOverlayMode(false);
      }
    };
    
    window.addEventListener('sidebarUpdate', handleSidebarUpdate);
    return () => window.removeEventListener('sidebarUpdate', handleSidebarUpdate);
  }, []);
  
  // Find the current page config
  const currentPageConfig = navigationConfig.find(item => item.id === activePage) || 
                           bottomNavigationConfig.find(item => item.id === activePage);
  const TabComponent = currentPageConfig?.tabs?.[activeTab]?.component;
  const tabKey = currentPageConfig?.tabs?.[activeTab]?.label || activeTab;

  return (
    <Layout 
      activePage={activePage} 
      onPageChange={handlePageChange}
      headerTabs={pageTabs}
      activeHeaderTab={activeTab}
      onHeaderTabChange={handleHeaderTabChange}
      rightSidebar={rightSidebar}
      overlayMode={overlayMode}
    >
      {TabComponent ? <PageWrapper title={currentPageConfig?.tabs?.[activeTab]?.label}><TabComponent key={tabKey} /></PageWrapper> : null}
    </Layout>
  );
};

// Session handling wrapper component
const SessionHandler = ({ children }) => {
  const { sessionExpired } = useAuth();
  
  return (
    <>
      {children}
      <SessionExpiredDialog open={sessionExpired} />
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <SessionHandler>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/promotions/:storeId" element={<Promotions />} />
              <Route path="/promotions/preview/:storeId" element={<Promotions preview={true} />} />
              <Route path="/*" element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              } />
            </Routes>
          </SessionHandler>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
